import clsx from 'clsx';
import { useTheme } from './useTheme.js';
import './ThemeQuickToggle.css';

export interface ThemeQuickToggleProps {
  className?: string;
  /** Optional aria-label override (e.g. localized "Switch to dark mode"). */
  ariaLabel?: string;
  /** Optional title attribute (tooltip on hover). */
  title?: string;
}

/**
 * Single-tap dark-mode toggle. Designed for tight top-bar real estate —
 * one icon, one tap, no labels, no menu. Cycles between light and dark
 * directly; the third "system" option lives in /settings for users who
 * want to track their OS preference.
 *
 * The icon swaps with the current theme:
 *   light → moon (☾)   ↔  "tap to go dark"
 *   dark  → sun  (☀)   ↔  "tap to go light"
 *
 * `system` is read as "what does prefers-color-scheme say?" — we resolve
 * it once on mount via matchMedia so the icon reflects the actual color
 * the user is seeing.
 */
export function ThemeQuickToggle({ className, ariaLabel, title }: ThemeQuickToggleProps) {
  const { theme, setTheme } = useTheme();

  // What the user is *actually* seeing right now. `system` resolves via
  // matchMedia; explicit values pass through.
  const effective: 'light' | 'dark' = (() => {
    if (theme === 'light') return 'light';
    if (theme === 'dark') return 'dark';
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  })();

  const next = effective === 'dark' ? 'light' : 'dark';
  const glyph = effective === 'dark' ? '☀' : '☾';

  return (
    <button
      type="button"
      className={clsx('fluent-theme-quick', className)}
      data-theme-current={effective}
      onClick={() => setTheme(next)}
      aria-label={ariaLabel ?? `Switch to ${next} mode`}
      title={title ?? ariaLabel ?? `Switch to ${next} mode`}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}
